import * as React from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CustomSetting } from 'types/types';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '45%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(2)
  },
  helperText: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: theme.spacing(1.4),
    lineHeight: theme.spacing(2.3),
    marginLeft: 0
  },
  textField: {
    width: '100%'
  },
  inputTextField: {
    fontSize: 13
  },
  highlightedTextField: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
      borderWidth: 2
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main
    }
  }
}));

type FilterTextFieldSectionProps = {
  inputSetting: CustomSetting;
  initialSelectValue: string[] | undefined;
  setSave: React.Dispatch<
    React.SetStateAction<Record<string, string | boolean | string[]>>
  >;
};

const parseCsv = (raw: string): string[] =>
  raw
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

export const FilterTextFieldSection: React.FC<FilterTextFieldSectionProps> = ({
  inputSetting,
  initialSelectValue,
  setSave
}: FilterTextFieldSectionProps) => {
  const classes = useStyles();
  const id = inputSetting.id as string;

  const [text, setText] = useState<string>(
    Array.isArray(initialSelectValue) ? initialSelectValue.join(', ') : ''
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
    },
    []
  );

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!id) return;
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      if (
        !Array.isArray(initialSelectValue) ||
        initialSelectValue.length === 0
      ) {
        return;
      }
    }
    setSave((prev) => ({ ...prev, [id]: parseCsv(text) }));
  }, [id, text, setSave, initialSelectValue]);

  const showError = inputSetting.highlighted && text.trim() === '';

  return (
    <div className={classes.container} data-testid="filter-textfield-section">
      <TextField
        size="small"
        className={`${classes.textField}${
          showError ? ` ${classes.highlightedTextField}` : ''
        }`}
        inputProps={{ className: classes.inputTextField }}
        value={text}
        onChange={handleChange}
        helperText={inputSetting?.helperTextValue}
        FormHelperTextProps={{ className: classes.helperText }}
        data-testid="filter-textfield-input"
        variant={inputSetting.highlighted ? 'outlined' : undefined}
      />
    </div>
  );
};
